import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { measurementAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import { Link } from 'react-router-dom';

const MeasurementForm = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const [measurement, setMeasurement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) loadMeasurement();
  }, [id]);

  const loadMeasurement = async () => {
    try {
      const response = await measurementAPI.getOne(id);
      setMeasurement(response.data.data || response.data);
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!measurement) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {t('measurements.notFound')}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('measurements.details')}</h2>
          <p className="text-gray-600 mt-1">{t('measurements.viewInfo')}</p>
        </div>
        <button
          onClick={() => navigate('/measurements')}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200"
        >
          {t('measurements.backToList')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8 space-y-6">
        {/* Order and Customer Info */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('measurements.orderInformation')}</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.salesOrder')}</label>
              {measurement.sales_order ? (
                <Link
                  to={`/sales-orders/${measurement.sales_order.id}`}
                  className="text-lg font-medium text-blue-600 hover:text-blue-900"
                >
                  {measurement.sales_order.order_number}
                </Link>
              ) : (
                <div className="text-lg text-gray-400">{t('measurements.na')}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.customer')}</label>
              <div className="text-lg font-medium text-gray-900">
                {measurement.customer?.name || t('measurements.na')}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.recordedBy')}</label>
              <div className="text-lg text-gray-900">
                {measurement.recorder?.full_name || t('measurements.na')}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.recordedDate')}</label>
              <div className="text-lg text-gray-900">
                {new Date(measurement.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* Body Measurements */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('measurements.bodyMeasurements')}</h3>
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.length')}</label>
              <div className="text-2xl font-bold text-gray-900">
                {measurement.length || '-'}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.chest')}</label>
              <div className="text-2xl font-bold text-gray-900">
                {measurement.chest || '-'}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.waist')}</label>
              <div className="text-2xl font-bold text-gray-900">
                {measurement.waist || '-'}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.hips')}</label>
              <div className="text-2xl font-bold text-gray-900">
                {measurement.hips || '-'}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.shoulder')}</label>
              <div className="text-2xl font-bold text-gray-900">
                {measurement.shoulder || '-'}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.sleeve')}</label>
              <div className="text-2xl font-bold text-gray-900">
                {measurement.sleeve || '-'}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('measurements.neck')}</label>
              <div className="text-2xl font-bold text-gray-900">
                {measurement.neck || '-'}
              </div>
            </div>
          </div>
        </div>

        {/* Special Notes */}
        {measurement.special_notes && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('measurements.specialNotes')}</h3>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-gray-700 whitespace-pre-wrap">{measurement.special_notes}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeasurementForm;
